import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { toast } from "react-toastify";
import { fetchAdmissionsByClientId } from "views/admin/Admissions/admissionListApi";
import {
  getMapping,
  listFiles,
  searchSendClients,
  sendCollegeDocuments,
} from "utils/collegeDocumentService";
import { libraryErrorMessage } from "./libraryErrorMessage";
import {
  buildOverridesPayload,
  collectPendingOverrideFields,
  parseMissingFieldsError,
} from "./sendOverridesUtils";
import {
  FILES_QUERY_KEY,
  MAPPING_QUERY_KEY,
  SEND_ADMISSIONS_QUERY_KEY,
  SEND_CLIENTS_QUERY_KEY,
  SENDS_QUERY_KEY,
} from "./documentLibraryQueryKeys";

const MIN_CLIENT_QUERY_LENGTH = 2;
const CLIENT_SEARCH_DEBOUNCE_MS = 300;

const initialState = {
  selectedFileIds: [],
  clientQuery: "",
  client: null,
  admissionId: "",
  message: "",
  // Per-file field-name -> typed value, mirroring the backend's `SendRequest.overrides` shape
  // exactly (see `CollegeDocumentSendService`), plus the parsed detail of the last blocking-gap
  // 400 (or null), which drives which fields this section shows before the admin has typed
  // anything into them.
  overridesByFile: {},
  blockingGap: null,
};

/** Open admissions only, this college's first: those are the ones a mapped form is usually for. */
const buildAdmissionOptions = (admissions, universityId) => {
  const open = (admissions || []).filter((admission) => admission.completeStatus !== true);
  const mine = open.filter((admission) => String(admission.university) === String(universityId));
  const others = open.filter((admission) => String(admission.university) !== String(universityId));
  return [...mine, ...others];
};

/**
 * Owns every piece of state, query and mutation the send dialog needs, so
 * `SendCollegeDocumentsDialog` itself stays a pure render of whatever this hook returns.
 */
export const useSendDocumentsDialog = ({
  open,
  universityId,
  folderId,
  preselectedFileId,
  onClose,
  onSent,
}) => {
  const queryClient = useQueryClient();
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (open) {
      setState({ ...initialState, selectedFileIds: preselectedFileId ? [preselectedFileId] : [] });
    }
  }, [open, preselectedFileId]);

  const filesQuery = useQuery({
    queryKey: [FILES_QUERY_KEY, universityId, folderId],
    queryFn: () => listFiles(universityId, folderId),
    enabled: open && Boolean(folderId),
  });

  // The picker searches on names, emails and passport numbers, so an undebounced key would send
  // one request per keystroke for every character of a client's name.
  const settledClientQuery = useDebouncedValue(state.clientQuery, CLIENT_SEARCH_DEBOUNCE_MS);
  const clientsQuery = useQuery({
    queryKey: [SEND_CLIENTS_QUERY_KEY, settledClientQuery],
    queryFn: () => searchSendClients(settledClientQuery),
    enabled: open && settledClientQuery.trim().length >= MIN_CLIENT_QUERY_LENGTH,
  });

  // `/student/client/{id}/admissions` is gated on `admissions:admission:read`, a permission this
  // feature's own `university:document:*` keys don't imply. A sender lacking it must see that the
  // lookup failed, not be told the client has no open admissions: `admissionOptions` alone can't
  // tell those two cases apart, so the loading/error state has to be surfaced separately.
  const admissionsQuery = useQuery({
    queryKey: [SEND_ADMISSIONS_QUERY_KEY, state.client?.id],
    queryFn: () => fetchAdmissionsByClientId(state.client.id),
    enabled: open && Boolean(state.client?.id),
  });

  const admissionOptions = useMemo(
    () => buildAdmissionOptions(admissionsQuery.data, universityId),
    [admissionsQuery.data, universityId]
  );

  useEffect(() => {
    setState((current) => ({
      ...current,
      admissionId: admissionOptions.length === 1 ? String(admissionOptions[0].id) : "",
    }));
  }, [admissionOptions]);

  const mappedFiles = (filesQuery.data || []).filter(
    (file) => file.mappedFieldCount > 0 && file.active !== false
  );

  // Loaded once per selected file (not per keystroke or field edit) so the dialog knows, ahead of
  // a send attempt, which prefill-linked fields belong to which file: needed both to flag
  // admission-linked fields up front and to translate a blocking-gap error's field labels back
  // into the field names the backend's `overrides` map is keyed by.
  const fieldMappingQueries = useQueries({
    queries: state.selectedFileIds.map((fileId) => ({
      queryKey: [MAPPING_QUERY_KEY, fileId],
      queryFn: () => getMapping(fileId),
      enabled: open && Boolean(fileId),
    })),
  });

  const fieldsByFileId = useMemo(() => {
    const map = {};
    state.selectedFileIds.forEach((fileId, index) => {
      map[fileId] = fieldMappingQueries[index]?.data?.fields || [];
    });
    return map;
  }, [state.selectedFileIds, fieldMappingQueries]);

  const fileNameById = useMemo(() => {
    const map = {};
    (filesQuery.data || []).forEach((file) => {
      map[file.id] = file.name;
    });
    return map;
  }, [filesQuery.data]);

  const pendingOverrideFields = useMemo(
    () =>
      collectPendingOverrideFields({
        selectedFileIds: state.selectedFileIds,
        admissionId: state.admissionId,
        blockingGap: state.blockingGap,
        fieldsByFileId,
        fileNameById,
      }),
    [state.selectedFileIds, state.admissionId, state.blockingGap, fieldsByFileId, fileNameById]
  );

  const sendMutation = useMutation({
    mutationFn: () =>
      sendCollegeDocuments({
        universityId: Number(universityId),
        fileIds: state.selectedFileIds,
        clientId: state.client.id,
        studentId: state.admissionId ? Number(state.admissionId) : null,
        message: state.message || null,
        overrides: buildOverridesPayload(state.overridesByFile, state.selectedFileIds),
      }),
    onSuccess: (result) => {
      toast.success(`Sent ${result.documents.length} document(s)`);
      queryClient.invalidateQueries({ queryKey: [SENDS_QUERY_KEY] });
      onSent?.(result);
      onClose();
    },
    // A 400 naming missing fields is the one blocked-send outcome this dialog can recover from
    // without the admin leaving it: parse the file and field labels back out of the message and
    // let `pendingOverrideFields` turn them into inputs, so retrying is just fill-and-resend. Any
    // other failure still only gets the toast, same as before.
    onError: (error) => {
      toast.error(libraryErrorMessage(error, "Could not send"));
      const parsedGap = parseMissingFieldsError(libraryErrorMessage(error, ""));
      // The message names the file, not its id. Nothing stops two files in a folder sharing a
      // name, and guessing there would put the admin's typed value on the wrong document, so an
      // ambiguous match falls back to the plain error rather than offering inputs.
      const named = parsedGap
        ? mappedFiles.filter(
            (file) => file.name === parsedGap.fileName && state.selectedFileIds.includes(file.id)
          )
        : [];
      const gapFileId = named.length === 1 ? named[0].id : undefined;
      setState((current) => ({
        ...current,
        blockingGap:
          parsedGap && gapFileId !== undefined
            ? {
                fileId: gapFileId,
                fileName: parsedGap.fileName,
                labelList: parsedGap.labelList,
              }
            : null,
      }));
    },
  });

  const toggleFile = (fileId) =>
    setState((current) => ({
      ...current,
      selectedFileIds: current.selectedFileIds.includes(fileId)
        ? current.selectedFileIds.filter((id) => id !== fileId)
        : [...current.selectedFileIds, fileId],
    }));

  const setOverrideValue = (fileId, fieldName, value) =>
    setState((current) => ({
      ...current,
      overridesByFile: {
        ...current.overridesByFile,
        [fileId]: {
          ...current.overridesByFile[fileId],
          [fieldName]: value,
        },
      },
    }));

  return {
    mappedFiles,
    selectedFileIds: state.selectedFileIds,
    toggleFile,
    clientOptions: clientsQuery.data || [],
    client: state.client,
    // A typed override, or a still-open blocking gap, only ever means something for the client it
    // was typed or raised against: swapping clients without clearing both would risk one client's
    // passport number silently going out on another's document.
    setClient: (client) =>
      setState((current) => ({ ...current, client, overridesByFile: {}, blockingGap: null })),
    setClientQuery: (clientQuery) => setState((current) => ({ ...current, clientQuery })),
    admissionOptions,
    admissionsLoading: admissionsQuery.isLoading,
    admissionsError: admissionsQuery.isError
      ? libraryErrorMessage(admissionsQuery.error, "Could not load this client's admissions")
      : null,
    admissionId: state.admissionId,
    setAdmissionId: (admissionId) => setState((current) => ({ ...current, admissionId })),
    message: state.message,
    setMessage: (message) => setState((current) => ({ ...current, message })),
    pendingOverrideFields,
    overridesByFile: state.overridesByFile,
    setOverrideValue,
    sending: sendMutation.isLoading,
    canSubmit: Boolean(state.client) && state.selectedFileIds.length > 0,
    handleSend: () => sendMutation.mutate(),
  };
};
